import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Dog, Search, ChevronRight, Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ApisAIFooter from '@/components/ApisAIFooter';
import { API } from '@/utils/api';

const ALPHABET = 'ABCĆDEFGHIJKLŁMNOPQRSŚTUVWXYZŹŻ'.split('');

export default function BreedsListPage() {
  const [breeds, setBreeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeLetter, setActiveLetter] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/rasy`).then(res => {
      const sorted = (res.data.breeds || []).sort((a, b) => a.name.localeCompare(b.name, 'pl'));
      setBreeds(sorted);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let result = breeds;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(b => b.name.toLowerCase().includes(q));
    }
    if (activeLetter) {
      result = result.filter(b => b.name.charAt(0).toUpperCase() === activeLetter);
    }
    return result;
  }, [breeds, search, activeLetter]);

  // Grupowanie po pierwszej literze
  const grouped = useMemo(() => {
    const groups = {};
    filtered.forEach(b => {
      const letter = b.name.charAt(0).toUpperCase();
      if (!groups[letter]) groups[letter] = [];
      groups[letter].push(b);
    });
    return groups;
  }, [filtered]);

  const letters = Object.keys(grouped).sort((a, b) => a.localeCompare(b, 'pl'));

  // Litery z rasami (do podświetlenia)
  const lettersWithBreeds = useMemo(() => {
    const set = new Set();
    breeds.forEach(b => set.add(b.name.charAt(0).toUpperCase()));
    return set;
  }, [breeds]);

  return (
    <>
      <Helmet>
        <title>Rasy psów od A do Z - Hodowle i Szczenięta w Polsce | ApisAI</title>
        <meta name="description" content="Katalog ras psów w Polsce od A do Z. Znajdź hodowlę, szczenięta i mioty. Ponad 290 ras psów z opisami i hodowcami." />
        <link rel="canonical" href="https://apisai.pl/rasy" />
      </Helmet>

      <div className="min-h-screen bg-[#0B0F1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
        {/* Nav */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0B0F1A]/80 backdrop-blur-xl border-b border-white/5">
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo.svg" alt="ApisAI" className="w-9 h-9" />
              <span className="font-semibold text-lg text-white">ApisAI</span>
            </Link>
            <Link to="/dodaj-hodowle">
              <Button className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 rounded-full px-6 text-sm">
                <Plus className="w-4 h-4 mr-2" />
                Dodaj hodowlę
              </Button>
            </Link>
          </div>
        </nav>

        <div className="pt-28 pb-20">
          <div className="max-w-6xl mx-auto px-6">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-medium mb-6">
                <Dog className="w-4 h-4" />
                Katalog ras
              </div>
              <h1 className="text-4xl sm:text-5xl font-black text-white mb-4" data-testid="breeds-title">
                Rasy psów od A do Z
              </h1>
              <p className="text-lg text-gray-400 max-w-xl mx-auto">
                {breeds.length} ras psów. Znajdź hodowlę, sprawdź dostępne szczenięta i mioty.
              </p>

              <Link
                to="/grupy-fci"
                data-testid="breeds-list-fci-groups-link"
                className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 bg-gradient-to-r from-emerald-500/10 to-emerald-500/5 border border-emerald-500/30 hover:border-emerald-400/60 rounded-full text-sm text-emerald-400 hover:text-emerald-300 transition-all"
              >
                <span>🏛️</span>
                <span>Przeglądaj według <strong>10 grup FCI</strong></span>
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Search */}
            <div className="max-w-lg mx-auto mb-6">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  placeholder="Szukaj rasy... np. owczarek, labrador"
                  value={search}
                  onChange={e => { setSearch(e.target.value); setActiveLetter(null); }}
                  className="w-full bg-[#10182B] border border-white/10 rounded-full pl-12 pr-6 py-4 text-white placeholder-gray-500 focus:border-blue-500/50 focus:outline-none transition-colors"
                  data-testid="breed-search"
                />
              </div>
            </div>

            {/* Alfabet A-Z */}
            <div className="flex flex-wrap justify-center gap-1 mb-8" data-testid="alphabet-nav">
              <button
                onClick={() => setActiveLetter(null)}
                className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${!activeLetter ? 'bg-blue-500 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'}`}
              >
                A-Z
              </button>
              {ALPHABET.map(letter => {
                const hasBreeds = lettersWithBreeds.has(letter);
                const isActive = activeLetter === letter;
                return (
                  <button
                    key={letter}
                    onClick={() => { setActiveLetter(isActive ? null : letter); setSearch(''); }}
                    disabled={!hasBreeds}
                    className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${
                      isActive
                        ? 'bg-blue-500 text-white'
                        : hasBreeds
                          ? 'bg-white/5 text-gray-300 hover:bg-white/10 hover:text-white'
                          : 'bg-white/[0.02] text-gray-700 cursor-default'
                    }`}
                  >
                    {letter}
                  </button>
                );
              })}
            </div>

            {/* Wyniki */}
            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-20">
                <Dog className="w-16 h-16 text-gray-700 mx-auto mb-4" />
                <p className="text-gray-400">Nie znaleziono rasy "{search || activeLetter}"</p>
                <button onClick={() => { setSearch(''); setActiveLetter(null); }} className="mt-4 text-blue-400 hover:text-blue-300 text-sm">
                  Wyczyść filtr
                </button>
              </div>
            ) : (
              <div className="space-y-8" data-testid="breeds-list">
                {letters.map(letter => (
                  <div key={letter} id={`letter-${letter}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-2xl font-black text-blue-400">{letter}</span>
                      <div className="flex-1 h-px bg-white/5" />
                      <span className="text-xs text-gray-500">{grouped[letter].length} ras</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                      {grouped[letter].map((breed, i) => (
                        <motion.div
                          key={breed.slug}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: Math.min(i * 0.02, 0.3) }}
                        >
                          <Link
                            to={`/rasy/${breed.slug}`}
                            data-testid={`breed-link-${breed.slug}`}
                            className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all group ${
                              breed.has_content
                                ? 'bg-emerald-500/5 border border-emerald-500/20 hover:bg-emerald-500/10 hover:border-emerald-500/40'
                                : 'bg-white/[0.02] border border-white/5 hover:bg-white/5 hover:border-white/15'
                            }`}
                          >
                            {breed.has_content && <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />}
                            <span className={`flex-1 text-sm ${breed.has_content ? 'text-white font-medium' : 'text-gray-300'} group-hover:text-white transition-colors`}>
                              {breed.name}
                            </span>
                            <ChevronRight className="w-3.5 h-3.5 text-gray-600 group-hover:text-gray-400 transition-colors flex-shrink-0" />
                          </Link>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Info */}
            {!search && !activeLetter && (
              <div className="mt-8 flex items-center gap-3 text-sm text-gray-500">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                <span>= rasa z aktywnymi hodowlami w katalogu</span>
              </div>
            )}

            {/* Link do katalogu biznesowego */}
            <div className="mt-16 text-center p-8 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-blue-500/10 border border-emerald-500/20">
              <h2 className="text-xl font-bold text-white mb-2">Szukasz usługodawcy?</h2>
              <p className="text-gray-400 mb-4">Sprawdź nasz katalog firm — groomerzy, fotografowie, trenerzy i więcej.</p>
              <Link to="/katalog/uslugi">
                <Button className="bg-emerald-500 hover:bg-emerald-600 rounded-full px-8 text-sm">
                  Katalog Firm i Usług
                </Button>
              </Link>
            </div>
          </div>
        </div>

        <ApisAIFooter pageType="breeds" />
      </div>
    </>
  );
}
